
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserBusinessEntityCreditReportSettings from 'Models/Security/Acl/UserBusinessEntityCreditReportSettings';
import VisitorsBusinessEntityCreditReportSettings from 'Models/Security/Acl/VisitorsBusinessEntityCreditReportSettings';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IBusinessEntityCreditReportSettingsEntityAttributes extends IModelAttributes {
	isEnabled: boolean;

	businessEntityId: string;
	businessEntity: Models.BusinessEntity | Models.IBusinessEntityAttributes;
	providerProductOrganisationId: string;
	providerProductOrganisation: Models.ProviderProductOrganisationEntity | Models.IProviderProductOrganisationEntityAttributes;
	providerProductId: string;
	providerProduct: Models.ProviderProductEntity | Models.IProviderProductEntityAttributes;
	reportRequests: Array<
		| Models.ReportRequestEntity
		| Models.IReportRequestEntityAttributes
	>;

}

@entity('BusinessEntityCreditReportSettingsEntity', 'Business Entity Credit Report Settings')
export default class BusinessEntityCreditReportSettingsEntity extends Model
	implements IBusinessEntityCreditReportSettingsEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserBusinessEntityCreditReportSettings(),
		new VisitorsBusinessEntityCreditReportSettings(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<BusinessEntityCreditReportSettingsEntity, boolean>()
	@CRUD({
		name: 'Is Enabled',
		displayType: 'checkbox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public isEnabled: boolean;
	

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Business Entity',
		displayType: 'reference-combobox',
		order: 20,
		referenceTypeFunc: () => Models.BusinessEntity,
		
	})
	public businessEntityId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public businessEntity: Models.BusinessEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Provider Product Organisation',
		displayType: 'reference-combobox',
		order: 30,
		referenceTypeFunc: () => Models.ProviderProductOrganisationEntity,
		
	})
	public providerProductOrganisationId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public providerProductOrganisation: Models.ProviderProductOrganisationEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Provider Product',
		displayType: 'reference-combobox',
		order: 40,
		referenceTypeFunc: () => Models.ProviderProductEntity,
		
	})
	public providerProductId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public providerProduct: Models.ProviderProductEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Report Requests',
		displayType: 'reference-multicombobox',
		order: 50,
		referenceTypeFunc: () => Models.ReportRequestEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'reportRequests',
			oppositeEntity: () => Models.ReportRequestEntity,
		}),
		
	})
	public reportRequests: Models.ReportRequestEntity[] = [];



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IBusinessEntityCreditReportSettingsEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IBusinessEntityCreditReportSettingsEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.isEnabled !== undefined) {
				this.isEnabled = attributes.isEnabled;
			}
			if (attributes.businessEntityId !== undefined) {
				this.businessEntityId = attributes.businessEntityId;
			}
			if (attributes.businessEntity !== undefined) {
				if (attributes.businessEntity === null) {
					this.businessEntity = attributes.businessEntity;
				} else if (attributes.businessEntity instanceof Models.BusinessEntity) {
					this.businessEntity = attributes.businessEntity;
					this.businessEntityId = attributes.businessEntity.id;
				} else {
					this.businessEntity = new Models.BusinessEntity(attributes.businessEntity);
					this.businessEntityId = this.businessEntity.id;
				}
			}
			if (attributes.providerProductOrganisationId !== undefined) {
				this.providerProductOrganisationId = attributes.providerProductOrganisationId;
			}
			if (attributes.providerProductOrganisation !== undefined) {
				if (attributes.providerProductOrganisation === null) {
					this.providerProductOrganisation = attributes.providerProductOrganisation;
				} else if (attributes.providerProductOrganisation instanceof Models.ProviderProductOrganisationEntity) {
					this.providerProductOrganisation = attributes.providerProductOrganisation;
					this.providerProductOrganisationId = attributes.providerProductOrganisation.id;
				} else {
					this.providerProductOrganisation = new Models.ProviderProductOrganisationEntity(attributes.providerProductOrganisation);
					this.providerProductOrganisationId = this.providerProductOrganisation.id;
				}
			}
			if (attributes.providerProductId !== undefined) {
				this.providerProductId = attributes.providerProductId;
			}
			if (attributes.providerProduct !== undefined) {
				if (attributes.providerProduct === null) {
					this.providerProduct = attributes.providerProduct;
				} else if (attributes.providerProduct instanceof Models.ProviderProductEntity) {
					this.providerProduct = attributes.providerProduct;
					this.providerProductId = attributes.providerProduct.id;
				} else {
					this.providerProduct = new Models.ProviderProductEntity(attributes.providerProduct);
					this.providerProductId = this.providerProduct.id;
				}
			}
			if (attributes.reportRequests !== undefined && Array.isArray(attributes.reportRequests)) {
				for (const model of attributes.reportRequests) {
					if (model instanceof Models.ReportRequestEntity) {
						this.reportRequests.push(model);
					} else {
						this.reportRequests.push(new Models.ReportRequestEntity(model));
					}
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		businessEntity {
			${Models.BusinessEntity.getAttributes().join('\n')}
		}
		providerProductOrganisation {
			${Models.ProviderProductOrganisationEntity.getAttributes().join('\n')}
		}
		providerProduct {
			${Models.ProviderProductEntity.getAttributes().join('\n')}
		}
		reportRequests {
			${Models.ReportRequestEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			reportRequests: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(BusinessEntityCreditReportSettingsEntity.prototype, 'created');
CRUD(modifiedAttr)(BusinessEntityCreditReportSettingsEntity.prototype, 'modified');
